import { CSSProperties, FC, useState } from "react";
import { Carousel } from "@mantine/carousel";
import { Box, Group, Image, UnstyledButton, Overlay, Portal, Skeleton } from "@mantine/core";
import { useHover } from "@mantine/hooks";
import styles from "./styles.module.css";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { ArchivedDocumentWithDetails, ContractAttachment, DocumentBase64 } from "@/types";

// Union type for all supported image types
type ImageType = ArchivedDocumentWithDetails | File | ContractAttachment | DocumentBase64;

interface CarouselProps {
  images: ImageType[];
  height?: number;
  imagesPrSlide?: 1 | 2 | 3 | 4;
  onRemoveImage?: (fileName: string) => void;
  isLoading?: boolean;
}

/**
 * @description A responsive image carousel component that displays a collection of images.
 * It supports both local file objects and fetched image data (base64 strings).
 * @property {ImageType[]} images - An array of image data, which can be either File objects from user input or fetched image data.
 * @property {number} [height] - The fixed height of the carousel. Defaults to 'auto'.
 * @property {1 | 2 | 3 | 4} [imagesPrSlide=2] - The number of images to display per slide.
 * @property {(fileName: string) => void} [onRemoveImage] - A callback function invoked when a user clicks the remove button on an image. It receives the image's file name or identifier.
 */
const ImageCarousel: FC<CarouselProps> = ({
  images,
  height,
  imagesPrSlide = 2,
  onRemoveImage,
  isLoading = false,
}: CarouselProps) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);

  if (isLoading) {
    return (
      <Group>
        <Skeleton height={height ?? 150} width="45%" radius="xs" />
        <Skeleton height={height ?? 150} width="45%" radius="xs" />
      </Group>
    );
  }

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <>
      <Carousel
        classNames={{
          root: styles.root,
          control: styles.control,
          controls: styles.controls,
        }}
        withControls
        height={height ?? "auto"}
        slideSize={`${100 / imagesPrSlide}%`}
        slideGap="16px"
        emblaOptions={{ align: "start" }}
      >
        {images.map((image, index) => (
          <HoverableImageSlide
            key={getImageId(image, index)}
            image={image}
            index={index}
            onRemoveImage={onRemoveImage}
            setInitialSlide={setInitialSlide}
            setIsFullScreen={setIsFullScreen}
          />
        ))}
      </Carousel>

      {isFullScreen && (
        <ImageCarouselFullScreen images={images} initialSlide={initialSlide} onClose={() => setIsFullScreen(false)} />
      )}
    </>
  );
};

interface FullScreenProps {
  images: ImageType[];
  onClose: () => void;
  initialSlide?: number;
}

/**
 * @description A full-screen overlay component for viewing images.
 * It displays images in a carousel with navigation controls.
 * @property {ImageType[]} images - An array of image data.
 * @property {() => void} onClose - A callback function to close the full-screen view.
 * @property {number} [initialSlide] - The index of the slide to display first.
 */
const ImageCarouselFullScreen: FC<FullScreenProps> = ({ images, onClose, initialSlide = 0 }) => {
  return (
    <Portal>
      <Overlay
        backgroundOpacity={0.85}
        fixed
        style={{
          paddingLeft: "10%",
          paddingRight: "10%",
          alignContent: "center",
        }}
      >
        <UnstyledButton
          onClick={onClose}
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            padding: "10px",
            zIndex: 201,
            backgroundColor: "black",
          }}
        >
          <SVGAsset name="close-fullscreen" />
        </UnstyledButton>
        <Carousel
          classNames={{
            root: styles.rootFullscreen,
            controls: styles.controlsFullscreen,
            control: styles.controlFullscreen,
            indicators: styles.indicatorsFullscreen,
            indicator: styles.indicatorFullscreen,
          }}
          withControls
          withIndicators
          initialSlide={initialSlide}
          nextControlIcon={<SVGAsset name="full-screen-arrow" />}
          previousControlIcon={<SVGAsset name="full-screen-arrow" style={{ transform: "rotate(180deg)" }} />}
        >
          {images.map((image, index) => (
            <Carousel.Slide key={getImageId(image, index)}>
              {renderImage(image, {
                flex: 0,
                maxWidth: "80vw",
                maxHeight: "80vh",
              })}
            </Carousel.Slide>
          ))}
        </Carousel>
      </Overlay>
    </Portal>
  );
};

interface HoverableImageSlideProps {
  image: ImageType;
  index: number;
  onRemoveImage?: (fileName: string) => void;
  setInitialSlide: (index: number) => void;
  setIsFullScreen: (state: boolean) => void;
}

/**
 * @description A single slide component for the `ImageCarousel`.
 * It displays an image and interactive controls for removal and full-screen view on hover.
 * @property {ImageType} image - The image data to display.
 * @property {number} index - The slide index of the image.
 * @property {(fileName: string) => void} [onRemoveImage] - A callback to remove the image.
 * @property {(index: number) => void} setInitialSlide - A state setter to set the initial slide for the full-screen view.
 * @property {(state: boolean) => void} setIsFullScreen - A state setter to toggle the full-screen view.
 */
const HoverableImageSlide: FC<HoverableImageSlideProps> = ({
  image,
  index,
  onRemoveImage,
  setInitialSlide,
  setIsFullScreen,
}) => {
  const { hovered, ref } = useHover();

  const handleFullScreenClick = () => {
    setInitialSlide(index);
    setIsFullScreen(true);
  };

  const handleRemoveClick = () => {
    if (onRemoveImage) {
      const id = getImageId(image, index);
      onRemoveImage(id);
    }
  };

  return (
    <Carousel.Slide ref={ref}>
      <Box style={{ position: "relative", height: "100%" }}>
        {renderImage(image, { height: "100%" })}
        {hovered && (
          <Overlay>
            <Group
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <UnstyledButton onClick={handleFullScreenClick}>
                <SVGAsset name="full-screen" />
              </UnstyledButton>
              {onRemoveImage && (
                <UnstyledButton onClick={handleRemoveClick}>
                  <SVGAsset name="trashcan" />
                </UnstyledButton>
              )}
            </Group>
          </Overlay>
        )}
      </Box>
    </Carousel.Slide>
  );
};

// Utility functions
function isFile(item: ImageType): item is File {
  return item instanceof File;
}

function isDocumentBase64(item: ImageType): item is DocumentBase64 {
  return "url" in item && "base64" in item;
}

function isContractAttachment(item: ImageType): item is ContractAttachment {
  return "content" in item && !("base64" in item);
}

function isArchivedDocument(item: ImageType): item is ArchivedDocumentWithDetails {
  return "document" in item;
}

function getImageId(image: ImageType, fallbackIndex: number): string {
  if (isFile(image)) {
    return image.name;
  } else if (isDocumentBase64(image)) {
    return image.id;
  } else if (isContractAttachment(image)) {
    return image.id;
  } else if (isArchivedDocument(image)) {
    return image.id;
  }
  return `image-${fallbackIndex}`;
}

function getImageUrl(image: ImageType): string {
  if (isFile(image)) {
    return URL.createObjectURL(image);
  } else if (isDocumentBase64(image)) {
    return image.url || "";
  } else if (isContractAttachment(image)) {
    return `data:${image.mediaType};base64,${image.content}`;
  } else if (isArchivedDocument(image)) {
    return `data:${image.mediaType};base64,${image.document}`;
  }
  return "";
}

function renderImage(image: ImageType, style?: CSSProperties) {
  const url = getImageUrl(image);
  return <Image style={style} src={url} alt="Image" />;
}

export default ImageCarousel;
